import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getExerciseById, displayName } from "@/lib/exerciseLibrary";

export function ExerciseDetailDialog({ open, onOpenChange, libraryId }) {
  const [exercise, setExercise] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | missing

  useEffect(() => {
    if (!open || !libraryId) return;

    let cancelled = false;
    setStatus("loading");

    getExerciseById(libraryId)
      .then((found) => {
        if (cancelled) return;
        setExercise(found);
        setStatus(found ? "ready" : "missing");
      })
      .catch(() => {
        if (!cancelled) setStatus("missing");
      });

    return () => {
      cancelled = true;
    };
  }, [open, libraryId]);

  const name = exercise ? displayName(exercise) : "";
  const steps = exercise?.steps?.length ? exercise.steps : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-surface rounded-3xl border-0 max-w-md max-h-[85vh] overflow-y-auto p-0">
        {/* Radix exige un DialogTitle en todos los estados, o los lectores de
            pantalla anuncian un diálogo sin nombre. */}
        {status !== "ready" && (
          <DialogHeader className="sr-only">
            <DialogTitle>Detalle del ejercicio</DialogTitle>
            <DialogDescription>Información del ejercicio seleccionado</DialogDescription>
          </DialogHeader>
        )}

        {status === "loading" && (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-xs text-muted-foreground">Cargando ejercicio...</p>
          </div>
        )}

        {status === "missing" && (
          <div className="py-16 px-6 text-center">
            <p className="text-sm text-muted-foreground">Ejercicio no disponible en la biblioteca.</p>
          </div>
        )}

        {status === "ready" && exercise && (
          <>
            <div className="relative w-full aspect-[4/3] bg-secondary/40 overflow-hidden rounded-t-3xl">
              <img
                src={exercise.gifUrl}
                alt={name}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>

            <div className="p-6 space-y-4">
              <DialogHeader className="text-left space-y-1.5">
                <DialogTitle className="text-foreground text-xl font-bold tracking-tight">
                  {name}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Detalle del ejercicio {name}
                </DialogDescription>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {exercise.muscleGroup && (
                    <Badge className="bg-primary/20 text-primary border-0 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                      {exercise.muscleGroup}
                    </Badge>
                  )}
                  {exercise.equipment && (
                    <Badge className="bg-secondary/60 text-muted-foreground border-0 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                      {exercise.equipment}
                    </Badge>
                  )}
                  {exercise.bodyPart && (
                    <Badge className="bg-secondary/60 text-muted-foreground border-0 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                      {exercise.bodyPart}
                    </Badge>
                  )}
                </div>
              </DialogHeader>

              {steps && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Cómo se hace
                  </p>
                  <ol className="space-y-2">
                    {steps.map((step, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-foreground/85 leading-relaxed">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center mt-0.5">
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {!steps && exercise.instructions && (
                <p className="text-sm text-foreground/85 leading-relaxed">{exercise.instructions}</p>
              )}

              {exercise.secondaryMuscles?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Músculos secundarios
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {exercise.secondaryMuscles.map((m) => (
                      <Badge
                        key={m}
                        className="bg-secondary/40 text-muted-foreground border-0 rounded-full text-[10px] font-medium"
                      >
                        {m}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {exercise.attribution && (
                <p className="text-[10px] text-muted-foreground/50 pt-2 border-t border-border/30">
                  {exercise.attribution}
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
